import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { sanitizeField, isValidZip, isValidNumber, filterNameInput, filterZipInput, filterDigitsInput } from "@/lib/validation";

const PAYER_OPTIONS = ["Private Pay", "Medicaid", "Medicare", "LTC Insurance", "VA", "Workers Comp", "Other"];

export default function QuickIntake() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [zip, setZip] = useState("");
  const [payer, setPayer] = useState("");
  const [urgency, setUrgency] = useState("");
  const [hours, setHours] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  function validate(): boolean {
    const errs: Record<string, string> = {};
    const cleanName = sanitizeField(name, 200);
    if (!cleanName) errs.name = "Client name is required";
    else if (name.length > 200) errs.name = "Client name must be 200 characters or fewer";

    if (!zip.trim()) errs.zip = "ZIP code is required";
    else if (!isValidZip(zip)) errs.zip = "Enter a valid 5-digit ZIP code";

    if (!payer) errs.payer = "Payer source is required";

    if (!urgency) errs.urgency = "Urgency is required";
    else if (!isValidNumber(Number(urgency), 1, 5)) errs.urgency = "Urgency must be 1-5";

    if (!hours) errs.hours = "Hours/week is required";
    else if (!isValidNumber(Number(hours), 0, 168)) errs.hours = "Hours must be 0-168";

    if (notes.length > 2000) errs.notes = "Notes must be under 2000 characters";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  const canSubmit = !!name.trim() && !!zip.trim() && !!payer && !!urgency && !!hours;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!user || !validate()) return;
    setSaving(true);
    const { error } = await supabase.from("leads").insert({
      user_id: user.id,
      name: sanitizeField(name, 200),
      zip_code: zip.trim() || null,
      payer_source: payer || null,
      urgency: urgency ? Number(urgency) : null,
      hours_needed: hours ? Number(hours) : null,
      acuity_notes: sanitizeField(notes, 2000) || null,
      source: "manual",
    });
    setSaving(false);
    if (error) { toast.error("Failed to save lead"); return; }
    setSaved(true);
    toast.success("Lead saved!");
    setTimeout(() => {
      setName(""); setZip(""); setPayer(""); setUrgency(""); setHours(""); setNotes("");
      setSaved(false); setErrors({});
    }, 1500);
  }

  return (
    <div className="space-y-4">
      <div>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mb-2 flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <h2 className="text-xl font-bold text-foreground">Quick Intake</h2>
        <p className="text-sm text-muted-foreground">Add a new lead quickly from any device.</p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4 max-w-lg">
        <div className="space-y-1.5">
          <Label htmlFor="qi-name">Client Name *</Label>
          <Input id="qi-name" value={name} onChange={e => setName(filterNameInput(e.target.value))} required className="min-h-[48px] text-base" placeholder="Full name" maxLength={200} />
          {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="qi-zip">ZIP Code *</Label>
            <Input id="qi-zip" type="text" inputMode="numeric" required value={zip} onChange={e => setZip(filterZipInput(e.target.value))} className="min-h-[48px] text-base" placeholder="e.g. 22102" maxLength={10} />
            {errors.zip && <p className="text-xs text-destructive">{errors.zip}</p>}
          </div>
          <div className="space-y-1.5">
            <Label>Payer Source *</Label>
            <Select value={payer} onValueChange={setPayer}>
              <SelectTrigger className="min-h-[48px] text-base"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{PAYER_OPTIONS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
            {errors.payer && <p className="text-xs text-destructive">{errors.payer}</p>}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="qi-urgency">Urgency (1-5) *</Label>
            <Input id="qi-urgency" type="text" inputMode="numeric" required value={urgency} onChange={e => setUrgency(filterDigitsInput(e.target.value).slice(0, 1))} className="min-h-[48px] text-base" />
            {errors.urgency && <p className="text-xs text-destructive">{errors.urgency}</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="qi-hours">Hours/Week *</Label>
            <Input id="qi-hours" type="text" inputMode="numeric" required value={hours} onChange={e => setHours(filterDigitsInput(e.target.value).slice(0, 3))} className="min-h-[48px] text-base" />
            {errors.hours && <p className="text-xs text-destructive">{errors.hours}</p>}
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="qi-notes">Care Needs / Notes</Label>
          <Textarea id="qi-notes" value={notes} onChange={e => setNotes(e.target.value)} className="min-h-[80px] text-base" placeholder="Care needs, special instructions..." maxLength={2000} />
          {errors.notes && <p className="text-xs text-destructive">{errors.notes}</p>}
          <p className="text-xs text-muted-foreground text-right">{notes.length}/2000</p>
        </div>
        <Button type="submit" className="w-full min-h-[48px] text-base" disabled={saving || !canSubmit}>
          {saved ? <><CheckCircle2 className="h-5 w-5 mr-2" /> Saved!</> : saving ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Saving…</> : "Save Lead"}
        </Button>
      </form>
    </div>
  );
}
